import React from "react";
import { Button, Paper, Stack, Typography } from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import { t } from "i18next";
import { HerdStreakResults as HerdStreakResultsType, Player } from "../../shared/interfaces/GameLogicTypes";
import useSocketContext from "../../shared/useSocketContext";
import "./HerdStreakResults.css";

export interface HerdStreakResultsProps {
  streakResults: HerdStreakResultsType;
  players: { [playerId: string]: Player };
  isMobile?: boolean;
  waitForHost: boolean;
  isHost: boolean;
  isReady: boolean;
  readyPlayerIds: string[];
  totalPlayers: number;
  onReadyToggle: (next: boolean) => void;
}

interface PlayerRow {
  playerId: string;
  name: string;
  totalPoints: number;
  survivedAll: boolean;
  eliminatedOnTurn: number;
  turnResults: { survived: boolean; points: number }[];
}

export default function HerdStreakResultsPanel({
  streakResults,
  players,
  isMobile,
  waitForHost,
  isHost,
  isReady,
  readyPlayerIds,
  totalPlayers,
  onReadyToggle,
}: HerdStreakResultsProps) {
  const socket = useSocketContext().socket;

  // Build player rows
  const allPlayerIds = new Set<string>();
  streakResults.turnResults.forEach((tr) => {
    tr.survivors.forEach((id) => allPlayerIds.add(id));
    tr.eliminations.forEach((id) => allPlayerIds.add(id));
  });

  const rows: PlayerRow[] = Array.from(allPlayerIds).map((pid) => {
    const turnResults: PlayerRow["turnResults"] = [];
    let eliminatedOnTurn = -1;
    let survivedAll = true;

    streakResults.turnResults.forEach((tr, i) => {
      const wasEliminated = tr.eliminations.includes(pid);
      const wasSurvivor = tr.survivors.includes(pid);
      const points = tr.pointsAwarded[pid] ?? 0;

      if (wasEliminated) {
        eliminatedOnTurn = i;
        survivedAll = false;
      }

      if (wasSurvivor || wasEliminated) {
        turnResults.push({ survived: !wasEliminated, points });
      }
    });

    return {
      playerId: pid,
      name: players[pid]?.name ?? pid,
      totalPoints: streakResults.totalPointsByPlayer[pid] ?? 0,
      survivedAll,
      eliminatedOnTurn,
      turnResults,
    };
  });

  rows.sort((a, b) => {
    if (a.survivedAll && !b.survivedAll) return -1;
    if (!a.survivedAll && b.survivedAll) return 1;
    if (a.survivedAll && b.survivedAll) return b.totalPoints - a.totalPoints;
    return b.eliminatedOnTurn - a.eliminatedOnTurn;
  });

  const maxPoints = Math.max(...rows.map((r) => r.totalPoints), 1);

  // Check if last turn was a split
  const lastResult = streakResults.turnResults[streakResults.turnResults.length - 1];
  const endedWithSplit = lastResult?.wasSplit ?? false;

  return (
    <Paper elevation={3} sx={{ width: "100%", p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight="bold" textAlign="center">
          {t("herd_results")}
        </Typography>

        {endedWithSplit && (
          <Typography variant="body2" textAlign="center" sx={{ color: "#ffd54f", fontWeight: "bold" }}>
            {t("herd_split")}
          </Typography>
        )}

        {/* Bar chart */}
        <div className="herd-streak-results">
          {rows.map((row) => (
            <div key={row.playerId} className="herd-result-row">
              <div className="herd-result-name">{row.name}</div>
              <div className="herd-result-bar">
                {row.turnResults.map((tr, i) => {
                  const widthPct = Math.max(15, (tr.points / maxPoints) * 100);
                  return (
                    <div
                      key={i}
                      className={`herd-result-segment ${tr.survived ? "survived" : "eliminated"}`}
                      style={{
                        width: `${widthPct}%`,
                        backgroundColor: tr.survived
                          ? "rgba(146, 220, 103, 0.7)"
                          : "rgba(231, 81, 81, 0.7)",
                        animationDelay: `${i * 0.3}s`,
                      }}
                    >
                      {tr.points > 0 ? `+${tr.points}` : ""}
                    </div>
                  );
                })}
              </div>
              <div className="herd-result-end-marker">
                {row.survivedAll ? (
                  <FlagIcon sx={{ fontSize: 20, color: "#92dc67" }} />
                ) : (
                  <span role="img" aria-label="skull">&#128128;</span>
                )}
              </div>
              <div className="herd-result-points">{row.totalPoints} pts</div>
            </div>
          ))}
        </div>

        {/* Ready/Continue button */}
        <Stack alignItems="center">
          {waitForHost ? (
            isHost ? (
              <Button variant="contained" onClick={() => socket.emit("nextTurn")}>
                {t("continue")}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("waiting_for_host")}
              </Typography>
            )
          ) : (
            <Button
              variant={isReady ? "outlined" : "contained"}
              onClick={() => onReadyToggle(!isReady)}
            >
              {isReady ? t("unready") : t("ready")} ({readyPlayerIds.length}/{totalPlayers})
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
